import { useMemo } from "react";
import { InventoryPeriodInput } from "./InventoryPeriodInput";
import { PeriodLayout, previewPeriodCount } from "./periodLayout";
import {
  inventoryGroupTargets,
  effectiveInventoryTargets,
  materialsWithInventoryBounds,
  setGroupInventoryBound,
  copyInventoryTargetBound,
} from "./inventoryTargetViews";
import { Button, Stack, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { InventoryTargetsTable } from "./InventoryTargetsTable";
import { InventoryPeriodTable } from "./InventoryPeriodTable";
import {
  ConditionalInventoryTargets,
  PeriodInventoryTargets,
} from "./ruleParameters";

export const InventoryRulesEditor = ({
  rules,
  materials,
  onChange,
  disabled,
  periodLayout,
}: {
  rules: PeriodInventoryTargets[];
  materials: string[];
  onChange: (rules: PeriodInventoryTargets[]) => void;
  disabled: boolean;
  periodLayout: PeriodLayout;
}) => {
  const { t } = useTenantTranslation();
  const deployable = previewPeriodCount(periodLayout.deployable);
  const forecast = previewPeriodCount(periodLayout.forecast);
  const resolved = useMemo(
    () => effectiveInventoryTargets({ rules, deployable, forecast }),
    [rules, deployable, forecast]
  );
  const targetsByGroup = inventoryGroupTargets({
    resolved,
    deployable,
    forecast,
  });
  const previewMaterials = useMemo(
    () => materialsWithInventoryBounds(resolved),
    [resolved]
  );
  return (
    <Stack gap={2}>
      {periodLayout.deployable.maximum === 1 &&
      periodLayout.forecast.maximum === 0 ? null : (
        <Typography variant="body2" color="text.secondary">
          {t(
            periodLayout.forecast.maximum === 0
              ? "autoInventorySequenceHelp"
              : "autoInventoryPeriodsHelp"
          )}
        </Typography>
      )}
      <InventoryPeriodTable
        targetsByGroup={targetsByGroup}
        resolved={resolved}
        deployable={deployable}
        previewMaterials={previewMaterials}
        rules={rules}
        materials={materials}
        disabled={disabled}
        periodLayout={periodLayout}
        onChange={(group, material, bound, quantity) => {
          onChange(
            setGroupInventoryBound({ rules, group, material, bound, quantity })
          );
        }}
        onClear={(material, bound, scope) => {
          onChange(
            copyInventoryTargetBound({
              rules,
              material,
              bound,
              scope,
              quantity: null,
            })
          );
        }}
        onCopy={(material, bound, quantity, scope) => {
          onChange(
            copyInventoryTargetBound({
              rules,
              material,
              bound,
              quantity,
              scope,
            })
          );
        }}
      />
    </Stack>
  );
};

export const ConditionalInventoryEditor = ({
  rules,
  materials,
  onChange,
  disabled,
  periodLayout,
}: {
  rules: ConditionalInventoryTargets[];
  materials: string[];
  onChange: (rules: ConditionalInventoryTargets[]) => void;
  disabled: boolean;
  periodLayout: PeriodLayout;
}) => {
  const { t } = useTenantTranslation();
  return (
    <Stack gap={2}>
      {rules.map((rule, index) => {
        const update = (
          targets_by_period: ConditionalInventoryTargets["targets_by_period"]
        ) => {
          onChange(
            rules.map((rule, position) =>
              position === index ? { ...rule, targets_by_period } : rule
            )
          );
        };
        return (
          <Stack key={rule.rule_index} gap={1}>
            {Object.entries(rule.targets_by_period).map(([period, targets]) => (
              <Stack key={period} gap={1}>
                <Stack direction="row" gap={1}>
                  <InventoryPeriodInput
                    period={period}
                    periods={Object.keys(rule.targets_by_period)}
                    disabled={disabled}
                    onChange={(next) => {
                      update(
                        Object.fromEntries(
                          Object.entries(rule.targets_by_period).map(
                            ([key, targets]) => [
                              key === period ? next : key,
                              targets,
                            ]
                          )
                        )
                      );
                    }}
                  />
                  <Button
                    disabled={disabled}
                    onClick={() => {
                      update(
                        Object.fromEntries(
                          Object.entries(rule.targets_by_period).filter(
                            ([key]) => key !== period
                          )
                        )
                      );
                    }}
                  >
                    {t("delete")}
                  </Button>
                </Stack>
                <InventoryTargetsTable
                  targets={targets}
                  materials={materials}
                  disabled={disabled}
                  rules={[]}
                  fixedPeriods={rule.targets_by_period}
                  periodLayout={periodLayout}
                  onChange={(targets) => {
                    update({ ...rule.targets_by_period, [period]: targets });
                  }}
                />
              </Stack>
            ))}
            <Button
              disabled={disabled}
              onClick={() => {
                update({
                  ...rule.targets_by_period,
                  [Math.max(
                    0,
                    ...Object.keys(rule.targets_by_period).map(Number)
                  ) + 1]: [],
                });
              }}
            >
              {t("autoAddPeriod")}
            </Button>
          </Stack>
        );
      })}
      {rules.length === 0 ? (
        <Typography color="text.secondary">{t("noData")}</Typography>
      ) : null}
    </Stack>
  );
};
